import { Observable, WritableObservable } from "micro-observables";
import { CurrencyCode } from "@domain/currency/currency";
import { GroupDraft } from "@domain/group/groupDraft";
import { SubscriptionMode } from "@domain/group/shareroom/shareroom";

export const settingEditorSteps = ["CHECK", "PRICING"] as const;
export type SettingEditionStep = (typeof settingEditorSteps)[number];

export class SettingEditorController {
	private previousValues: GroupDraft[];
	private readonly currentValueIndex = new WritableObservable(0);

	readonly group: Observable<GroupDraft>;

	private _step = new WritableObservable<SettingEditionStep>("CHECK");
	readonly step = this._step.readOnly();

	constructor(initialGroupDraft: GroupDraft) {
		this.previousValues = [initialGroupDraft];
		this.group = this.currentValueIndex.transform((index) => this.previousValues[index]);
	}

	next(): void {
		const step = this.step.get();

		switch (step) {
			case "CHECK":
				this._step.set("PRICING");
				return;
			default:
				break;
		}
	}

	setCurrency(currency: CurrencyCode): void {
		this.updateGroup({ currency });
	}

	setSubscriptionMode(subscriptionMode: SubscriptionMode): void {
		this.updateGroup({ subscriptionMode });
	}

	setPrice(price: number): void {
		this.updateGroup({ price });
	}

	updateGroup(groupUpdate: Partial<GroupDraft>): void {
		const group: GroupDraft = { ...this.group.get(), ...groupUpdate };
		this.previousValues = [...this.previousValues.slice(0, this.currentValueIndex.get() + 1), group];
		this.currentValueIndex.set(this.currentValueIndex.get() + 1);
	}

	get isPresentationValid(): Observable<boolean> {
		return Observable.from(this.group).transform(([group]) => {
			if (
				!group.presentation?.hasText() ||
				!group.objective?.hasText() ||
				!group.targetAudience?.hasText() ||
				!group.schedule?.hasText() ||
				!group.formatCourse?.hasText()
			) {
				return false;
			}
			return true;
		});
	}

	get isPricingValid(): Observable<boolean> {
		return Observable.from(this.group).transform(([group]) => {
			if (!group.currency || !group.subscriptionMode || !group.price) {
				return false;
			}

			return true;
		});
	}
}
